import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import EngagementView from '../components/shared/EngagementView';
import WorkflowRefLink from '../components/shared/WorkflowRefLink';
import { ProvenanceBadge, provenanceOrigin } from '../components/shared/provenance';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import { fetchJSON } from '../utils/api';
import { nowLabel } from '../components/shared/NowName';
import { engagementTag } from '../utils/engagementName';

/**
 * One of MY engagements, read-only (docs/modules/my-records.md).
 *
 * SAME page as the management detail: the frame is
 * RequestDetailShell + RequestHeader and the body is the shared
 * EngagementView — exactly what /qm/engagements/:id renders — pointed at
 * /api/my/engagements/:id, which only answers for engagements this person is
 * on the team of. The ONE difference is capability: no RecordActionsCard, so
 * Edit and Delete simply do not exist here. Changes go through Management or
 * an Engagement Amendment request.
 *
 * Being refused is a normal outcome (a bookmarked link to someone else's
 * job), so it gets a plain explanation rather than a red "access denied" wall.
 */

const MyEngagementDetail = () => {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const [engagement, setEngagement] = useState(null);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`/api/my/engagements/${id}`);
        if (!cancelled) setEngagement(data.data);
      } catch (e) {
        if (cancelled) return;
        // fetchJSON surfaces the server's message; the guard replies 403 with
        // a sentence written for the reader, so show it as-is.
        if (/not on this engagement/i.test(e.message || '')) setDenied(true);
        else setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (denied) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <Link to="/my?tab=engagements" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4" /> My Records
        </Link>
        <div className="mt-4 bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-10 text-center">
          <p className="text-sm text-slate-600">You are not on this engagement’s team.</p>
          <p className="text-xs text-slate-500 mt-1">
            My Records only shows engagements you lead or are rostered on.
          </p>
        </div>
      </div>
    );
  }

  return (
    <RequestDetailShell
      backTo="/my?tab=engagements"
      backLabel="My Records"
      loading={!engagement}
      error={error}
    >
      {engagement && (
        <>
          <RequestHeader
            requestNo={nowLabel(engagement.entityName || engagement.engagementName, engagement.entityNameNow)}
            typeLabel={['Engagement', engagementTag(engagement.serviceProvided, engagement.engagementSeq, engagement.serviceDescription)]
              .filter(Boolean).join(' · ')}
            originLabel={engagement.sourceRequestNo
              ? <>registered by <WorkflowRefLink refNo={engagement.sourceRequestNo} className="text-sm" /></>
              : provenanceOrigin(engagement.source)}
            submittedAt={engagement.createdAt}
            badge={<ProvenanceBadge source={engagement.source} />}
          />

          {/* 管理頁在這個位置放 Edit/Delete 動作列；這一面沒有動作，只對能在
              Management 改的人給一條連過去的路——其他人連過去只會撞到權限牆。 */}
          {hasPermission('engagements:edit') && (
            <div className="mb-4 flex justify-end text-sm">
              <RefLink
                to={`/qm/engagements/${engagement.id}`}
                className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 whitespace-nowrap"
              >
                <ExternalLink className="w-4 h-4" /> Open in Management
              </RefLink>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-5">
            <EngagementView engagement={engagement} basePath={`/api/my/engagements/${engagement.id}`} />
          </div>
        </>
      )}
    </RequestDetailShell>
  );
};

export default MyEngagementDetail;
